import { FunctionComponent, ReactElement, useEffect, useRef, useState } from 'react';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
import {
	CategoryScale,
	Chart as ChartJS,
	Chart,
	Legend,
	LinearScale,
	LineElement,
	PointElement,
	Title,
	TimeScale,
	Tooltip,
	TooltipItem,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, Divider } from '@mui/material';
import { ChartViewType, AllCyclesResponse } from '@/api/dashboard/types';
import Backdrop from '@/components/ui/backdrop';
import { QueryDateRange } from '../../../types';
import AllCyclesChartTitle from './AllCyclesChartTitle';
import { formatTooltipTitle } from './utils';
import { useAllCyclesChartData } from './useAllCyclesChartData';
import { chartBackgroundPlugin } from '../../completed-cycles/chart/plugins';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	TimeScale,
	LineElement,
	Title,
	Tooltip,
	Legend
);

const colors = [
	'#009688',
	'#DE5308',
	'#659C35',
	'#DC2B67',
	'#0266BD',
	'#9C27B0',
	'#B2DFDB',
	'#FFC570',
	'#CEE6B3',
	'#F48FB1',
	'#6DBEF8',
	'#D6A4DE',
	'#00796B',
	'#A03808',
	'#4B7A2A',
	'#A71B58',
	'#014893',
	'#6A1B9A',
	'#80CBC4',
	'#F48B1F',
	'#A2D16B',
	'#EC6693',
	'#0393ED',
	'#BE71CB',
	'#005548',
	'#6B2606',
	'#2D5A1C',
	'#761348',
	'#013974',
	'#40107A',
];
interface AllCyclesChartProps {
	allCycles?: AllCyclesResponse;
	legendLabel?: string;
	dateRange?: QueryDateRange;
	exportType: string | null;
	onChartReady?: (ready: boolean) => void;
	isLoading: boolean;
}

const AllCyclesChart: FunctionComponent<AllCyclesChartProps> = ({
	allCycles,
	dateRange,
	exportType,
	onChartReady,
	isLoading,
}): ReactElement => {
	const { view } = allCycles || {};
	const { labels, dataset } = useAllCyclesChartData(allCycles, dateRange);

	const chartRef = useRef(null);
	const [chartImgSrc, setChartImgSrc] = useState<string | null>(null);
	const [imageLoaded, setImageLoaded] = useState<boolean>(false);
	const [readySent, setReadySent] = useState(false);

	useEffect(() => {
		if (chartImgSrc && imageLoaded && !readySent) {
			onChartReady?.(true);
			setReadySent(true);
		}
	}, [chartImgSrc, imageLoaded, onChartReady, readySent]);

	useEffect(() => {
		if (!exportType) {
			setReadySent(false);
		}
	}, [exportType]);

	const convertToImgSource = (blob: Blob | null) => {
		if (!blob) return;
		const url = URL.createObjectURL(blob);
		setChartImgSrc(url);
	};

	const loadChartImageUrl = () => {
		const chart = chartRef.current as unknown as Chart;
		const { canvas, ctx } = chart;

		ctx.globalCompositeOperation = 'destination-over';
		ctx.fillStyle = '#fff';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.canvas.toBlob(convertToImgSource);
	};

	const afterPrintHandler = () => {
		// In rear cases, simulate with CPU throttle 6x, the chart won't have time to automatically update.
		setTimeout(() => {
			(chartRef.current as unknown as Chart)?.update();
		}, 100);
	};

	useEffect(() => {
		window.addEventListener('afterprint', afterPrintHandler);
		return () => window.removeEventListener('afterprint', afterPrintHandler);
	}, []);

	useEffect(() => {
		if (exportType) {
			loadChartImageUrl();
		} else {
			setChartImgSrc(null);
			setImageLoaded(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [exportType]);

	const handleVisibilityChange = () => {
		if (!chartRef) return;
		(chartRef.current as unknown as Chart)?.render();
	};

	// This is logic is added beacuse changing browser tab in chrome makes chart disappear
	useEffect(() => {
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, []);

	return (
		<Box sx={{ position: 'relative' }}>
			{isLoading && (
				<Backdrop
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(255, 255, 255, 1)',
					}}
				/>
			)}
			<Box component="div" sx={{ height: '293px', displayPrint: 'none', position: 'relative' }}>
				<Line
					aria-label="cycles chart"
					ref={chartRef}
					plugins={[chartBackgroundPlugin]}
					options={{
						maintainAspectRatio: false,
						responsive: true,
						layout: { padding: 20 },
						plugins: {
							legend: {
								display: false,
							},
							tooltip: {
								callbacks: {
									title: (tooltipItems: TooltipItem<'line'>[]) =>
										formatTooltipTitle(tooltipItems, dateRange),
								},
							},
						},
						scales: {
							x: {
								offset: true,
								ticks: {
									maxRotation: view === ChartViewType.Hours ? 45 : 50,
									minRotation: view === ChartViewType.Hours ? 45 : 0,
								},
							},
							y: {
								beginAtZero: true,
								ticks: {
									precision: 0,
								},
							},
						},
					}}
					/* eslint-disable */
					data={{
						labels: labels?.map(({ text }) => text) || [],
						datasets: dataset
							? dataset.map((series, index) => ({
								label: series.name,
								data: series.datapoints || [],
								backgroundColor: colors[index % colors.length],
								borderColor: colors[index % colors.length],
							}))
							: [],
					}}
				/* eslint-enable */
				/>
			</Box>
			<Box sx={{ displayPrint: 'none' }}>
				<Box component={Divider} mt={3} mb={4} className="dashboard-item-content" />
				<AllCyclesChartTitle allCycles={allCycles} colors={colors} />
				<Box component={Divider} mt={3} mb={4} className="dashboard-item-content" />
			</Box>
			<Box
				component="div"
				sx={{
					position: 'relative',
					width: '100%',
					display: 'none',
					displayPrint: 'block',
				}}
			>
				<img
					onLoad={() => setImageLoaded(true)}
					src={chartImgSrc || ''}
					alt="chart"
					className="chart-image"
				/>
				<Box component={Divider} mt={3} mb={4} className="dashboard-item-content" />
				<AllCyclesChartTitle allCycles={allCycles} colors={colors} />
				<Box component={Divider} mt={3} mb={4} className="dashboard-item-content" />
			</Box>
		</Box>
	);
};

export default AllCyclesChart;
